
import React, { useEffect } from 'react';
import { useOrderForm } from '../hooks/useOrderForm';
import { OrderWithCustomer } from '../types/orderTypes';
import OrderFormCustomerSection from './OrderFormCustomerSection';
import OrderFormDetailsSection from './OrderFormDetailsSection';
import OrderFormProductsSection from './OrderFormProductsSection';
import OrderFormActions from './OrderFormActions';
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Info, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { useShopName } from '../../products/hooks/useShopName';

interface OrderAddFormProps {
  handleOrderAdded: (order: OrderWithCustomer) => void;
  handleBackToOrders: () => void;
  shopId?: string;
  organizationId?: string | null;
}

const OrderAddForm = ({ handleOrderAdded, handleBackToOrders, shopId, organizationId }: OrderAddFormProps) => {
  console.log('OrderAddForm rendering with shopId:', shopId, 'organizationId:', organizationId);
  const { shopName } = useShopName(shopId);

  const {
    customerId,
    customerName,
    selectedCustomer,
    customerPhone,
    customerEmail,
    customerCountry,
    customerRegion,
    customerMunicipality,
    orderNumber,
    orderDate,
    shippingAddress,
    notes,
    products,
    totalAmount,
    isSubmitting,
    isOrderNumberLoading,
    handleSelectCustomer,
    handleAddProduct,
    handleRemoveProduct,
    handleSubmit,
    setOrderNumber,
    setOrderDate,
    setShippingAddress,
    setNotes,
    setCustomerCountry,
    setCustomerRegion,
    setCustomerMunicipality
  } = useOrderForm({ handleOrderAdded, handleBackToOrders, shopId, organizationId });

  // Ajoutons un effet pour mieux tracer le shopId et les valeurs actuelles
  useEffect(() => {
    console.log('FORM EFFECT: OrderAddForm reçoit shopId:', shopId, 'organizationId:', organizationId);
    console.log('FORM EFFECT: Valeurs actuelles:', {
      customerCountry,
      customerRegion,
      customerMunicipality,
      shippingAddress
    });
    
    // Set today's date as default
    if (!orderDate) {
      const today = new Date().toISOString().split('T')[0];
      setOrderDate(today);
    }
  }, [shopId, organizationId, orderDate, setOrderDate, customerCountry, customerRegion, customerMunicipality, shippingAddress]);

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Nouvelle commande {shopName ? `(${shopName})` : ''}
        </h1>
        {isOrderNumberLoading && (
          <div className="flex items-center text-amber-600">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Génération du numéro de commande...
          </div>
        )}
      </div>
      
      {!customerPhone && (
        <Alert className="mb-6 bg-amber-50 border-amber-200">
          <Info className="h-4 w-4 text-amber-500" />
          <AlertTitle className="text-amber-700">Important</AlertTitle>
          <AlertDescription className="text-amber-600">
            Veuillez sélectionner un client pour pouvoir créer la commande
          </AlertDescription>
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <OrderFormCustomerSection
            orderNumber={orderNumber}
            orderDate={orderDate}
            handleOrderNumberChange={setOrderNumber}
            handleOrderDateChange={setOrderDate}
            handleSelectCustomer={handleSelectCustomer}
            selectedCustomerId={customerId}
            selectedCustomerName={customerName !== 'Client par défaut' ? customerName : undefined}
            selectedCustomer={selectedCustomer}
            shopId={shopId}
          />
          
          <OrderFormDetailsSection
            shippingAddress={shippingAddress}
            notes={notes}
            handleShippingAddressChange={setShippingAddress}
            handleNotesChange={setNotes}
            customerCountry={customerCountry}
            customerRegion={customerRegion}
            customerMunicipality={customerMunicipality}
            customerEmail={customerEmail}
            customerPhone={customerPhone}
            setCustomerCountry={setCustomerCountry}
            setCustomerRegion={setCustomerRegion}
            setCustomerMunicipality={setCustomerMunicipality}
          />
          
          <OrderFormProductsSection
            products={products}
            totalAmount={totalAmount}
            handleAddProduct={handleAddProduct}
            handleRemoveProduct={handleRemoveProduct}
            shopId={shopId} 
            shopName={shopName}
          />
        </div>
        
        <OrderFormActions
          handleBackToOrders={handleBackToOrders}
          isSubmitting={isSubmitting}
          isEditing={false}
          isValid={!!customerPhone && products.length > 0}
        />
      </form>
    </div>
  );
};

export default OrderAddForm;
